import type { Category, Todo } from "../../domain/entities.ts";
import { applyBoardAutomations } from "./board-configuration.ts";
import { addApprovalEvent, approvalApplies, approvalStatus, requestOnReviewEntry } from "./task-approvals.ts";
import { activeTags } from "./selectors.ts";

export interface TaskMutationCapabilities {
  activeRoot: () => string | null;
  task: (id: string) => Todo | undefined;
  category: (id: string | null) => Category | undefined;
  persist: (id: string, rootId: string, update: (todo: Todo) => void) => Promise<Todo>;
  replaceCurrent: (todo: Todo) => void;
  replaceAvailable: (todo: Todo) => void;
  canEdit: (id: string) => boolean;
  createNextOccurrence: (todo: Todo) => Promise<string | null>;
  today: () => string;
  now: () => number;
  userId: () => string | undefined;
  markLocal: () => void;
}

export interface TaskMutationController {
  mutate: (id: string, update: (todo: Todo) => void) => Promise<Todo | undefined>;
  applyScheduledBoardAutomations: (todos: Todo[], categories: Category[]) => Promise<boolean>;
  complete: (todo: Todo) => Promise<string | null>;
}

export function createTaskMutationController(capabilities: TaskMutationCapabilities): TaskMutationController {
  async function mutate(id: string, update: (todo: Todo) => void): Promise<Todo | undefined> {
    const previous = capabilities.task(id);
    const rootId = previous?.rootId || capabilities.activeRoot();
    if (!rootId) return undefined;
    const snapshot = previous ? structuredClone(previous) : undefined;
    const projection = await capabilities.persist(id, rootId, todo => {
      update(todo);
      const category = capabilities.category(todo.categoryId);
      const userId = capabilities.userId();
      const changedCollection = !!snapshot && snapshot.categoryId !== todo.categoryId;
      if (userId && changedCollection && category?.approvalRule?.reviewerIds.length && (!category.approvalRule.column || activeTags(todo)[0] === category.approvalRule.column)) {
        if (approvalStatus(todo) !== "requested") addApprovalEvent(todo, "requested", userId, "", capabilities.now());
        todo.completed = false; todo.completedAt = null;
      }
      if (userId && !changedCollection && category?.approvalRule?.column) requestOnReviewEntry(todo, category, activeTags(snapshot || todo)[0] || null, activeTags(todo)[0] || null, userId, capabilities.now());
      const pendingReview = approvalApplies(category, todo) && approvalStatus(todo) !== "approved";
      if (category?.boardColumns?.length) {
        const settings = pendingReview && category.boardAutomation ? { ...category.boardAutomation, completedColumn: null, completeWithSubtasks: false } : category.boardAutomation;
        applyBoardAutomations(todo, snapshot, category.boardColumns || [], settings, capabilities.today(), capabilities.now());
      }
      if (pendingReview && !snapshot?.completed && todo.completed) { todo.completed = false; todo.completedAt = null; }
    });
    capabilities.replaceCurrent(projection);
    capabilities.replaceAvailable(projection);
    capabilities.markLocal();
    return projection;
  }

  async function applyScheduled(todos: Todo[], categories: Category[]): Promise<boolean> {
    let changed = false;
    for (let index = 0; index < todos.length; index += 1) {
      const original = todos[index];
      const category = categories.find(item => item.id === original.categoryId);
      if (!category?.boardColumns?.length || !capabilities.canEdit(original.id)) continue;
      const candidate = structuredClone(original);
      const pendingReview = approvalApplies(category, candidate) && approvalStatus(candidate) !== "approved";
      const settings = pendingReview && category.boardAutomation ? { ...category.boardAutomation, completedColumn: null, completeWithSubtasks: false } : category.boardAutomation;
      const events = applyBoardAutomations(candidate, original, category.boardColumns || [], settings, capabilities.today(), capabilities.now());
      if (!events.length) continue;
      const projection = await capabilities.persist(original.id, original.rootId, todo => Object.assign(todo, candidate));
      todos[index] = projection;
      capabilities.replaceAvailable(projection);
      if (!original.completed && candidate.completed) await capabilities.createNextOccurrence(candidate);
      changed = true;
    }
    return changed;
  }

  async function complete(todo: Todo): Promise<string | null> {
    const updated = await mutate(todo.id, draft => {
      draft.completed = true;
      draft.completedAt = capabilities.now();
    });
    return updated?.completed ? capabilities.createNextOccurrence(updated) : null;
  }

  return { mutate, applyScheduledBoardAutomations: applyScheduled, complete };
}
